import * as R from 'ramda';

import sportsApi from '../../utils/sportsApi';
import {
    getPlacementActive,
    getSponsorActiveAtPlacement,
} from '../../utils/getActiveOnboardingInstances';

import { STANDARD_PLACEMENT_EXPERIENCE } from '../../constants/config';

const getClientOnboarding = store => async (
    state,
    clientId,
    hideInactiveAssets = false,
) => {
    const userClientId = state.user.selectedUserClientId;
    const client = state.user.clientsById[clientId];
    const hasStandardPlacements = client.permissions.includes(
        STANDARD_PLACEMENT_EXPERIENCE,
    );

    const rspEndpoint = `/clients/${clientId}/rsps`;
    const rightsholderEndpoint = `/clients/${clientId}/rightsholders?includeSportId=true`;

    const [rspResponse, rightsholdersList] = await Promise.all([
        sportsApi
            .url(rspEndpoint)
            .get()
            .json(),
        sportsApi
            .url(rightsholderEndpoint)
            .get()
            .json(),
    ]);

    const rspRhIds = rspResponse.reduce((acc, el) => {
        const {
            rightsholder: { id },
        } = el;
        if (acc.findIndex(i => i === id) > -1) return acc;
        acc.push(id);
        return acc;
    }, []);

    const getRhSponsors = async id => {
        const spEndPoint = `/rightsholders/${id}/sponsors?includeInactive=true&clientId=${clientId}`;

        const sps = await sportsApi
            .url(spEndPoint)
            .get()
            .json();

        return { id: id, sponsors: sps };
    };

    const sponsorsPromises = rspRhIds.map(id => getRhSponsors(id));
    const rhSponsors = await Promise.all(sponsorsPromises);

    const rightsholdersWithRsp = rspResponse.reduce((acc, el) => {
        const {
            rightsholder,
            sponsor,
            placement,
            active: rspActive,
            clientRemoved: rspClientRemoved,
        } = el;
        const rh = acc.find(e => e.id === rightsholder.id);
        const sps = rhSponsors.find(e => e.id === rightsholder.id).sponsors;
        const fullSponsor = sps.find(s => s.id === sponsor.id);
        const logos =
            fullSponsor && fullSponsor.logos ? fullSponsor.logos : null;
        const analysisActive = fullSponsor && fullSponsor.analysisActive;

        const sp = {
            id: sponsor.id,
            name: sponsor.name,
            clientRemoved: rspClientRemoved,
            active: rspActive,
            logos,
            analysisActive,
        };

        if (!rh) {
            acc.push({
                id: rightsholder.id,
                placements: [
                    {
                        id: placement.id,
                        name: placement.name,
                        sponsors: [sp],
                    },
                ],
            });
        } else {
            const pl = rh.placements.find(e => e.id === placement.id);
            if (!pl) {
                rh.placements.push({
                    id: placement.id,
                    name: placement.name,
                    sponsors: [sp],
                });
            } else {
                pl.sponsors.push(sp);
            }
        }
        return acc;
    }, []);

    const onboardingRightsholders = rightsholdersList.reduce((acc, rh) => {
        const {
            id,
            name,
            sportId,
            mainLogo,
            maxSponsors,
            maxPlacements,
            leagueId,
        } = rh;
        const rhWithRsp = rightsholdersWithRsp.find(r => rh.id === r.id);
        if (rhWithRsp) {
            const placements = rhWithRsp.placements;
            acc.push({
                id,
                name,
                sportId,
                placements,
                mainLogo,
                maxSponsors,
                maxPlacements,
                leagueId,
            });
        } else {
            acc.push({
                id,
                name,
                sportId,
                placements: [],
                mainLogo,
                maxSponsors,
                maxPlacements,
                leagueId,
            });
        }
        return acc;
    }, []);

    const updatePlacements = async rh => {
        const { placements: plcs } = rh;

        const plEndPoint = `/clients/${clientId}/rightsholders/${rh.id}/placements`;

        const pls = await sportsApi
            .url(plEndPoint)
            .query({ includePlacementTypeExamples: true })
            .get()
            .json();

        const placements = pls.reduce((acc, pl) => {
            const { rightsholderPlacement: plt, customName } = pl;

            const {
                id,
                name,
                placementType,
                examples,
                active,
                clientRemoved,
                placementTypeExampleImages: reloImages = [],
            } = plt;

            let placementReloName = '';
            let placementDisplayName = '';

            if (hideInactiveAssets && !getPlacementActive(plt)) return acc;

            if (hasStandardPlacements) {
                placementReloName = name;
                placementDisplayName = customName ? customName : name;
            } else {
                placementDisplayName = name;
            }

            const plc = plcs.find(p => p.id === id);

            const sponsors =
                plc && plc.sponsors
                    ? hideInactiveAssets
                        ? R.filter(getSponsorActiveAtPlacement, plc.sponsors)
                        : plc.sponsors
                    : [];
            acc.push({
                id,
                name: placementDisplayName,
                placementType,
                examples,
                sponsors,
                active,
                clientRemoved,
                reloName: placementReloName,
                reloImages,
            });

            return acc;
        }, []);

        return { ...rh, placements };
    };

    const rhsPromises = onboardingRightsholders.reduce((acc, r) => {
        acc.push(updatePlacements(r));
        return acc;
    }, []);

    const rightsholders = await Promise.all(rhsPromises);

    const onboarding = { rightsholders };

    store.setState(
        R.assocPath(
            ['clients', userClientId, 'settings', 'client', 'onboarding'],
            onboarding,
            store.getState(),
        ),
        true,
    );
};

export default getClientOnboarding;
